/* ============================================================
   MATTER LANDING — CONFIGURATOR + CART
   ------------------------------------------------------------
   Reads the selling_plan_groups data injected by Liquid and
   matches the user's frequency + prepay-term choice to the
   right PayWhirl selling plan ID. Submits to /cart/add.
   ============================================================ */

(function () {
  'use strict';

  /* ============================================================
     PLAN_OVERRIDES — source of truth for (frequency, term) → PayWhirl plan ID
     ------------------------------------------------------------
     Declared at the top of the IIFE so it's available to all
     functions below (filterTermsForFreq, resolveSellingPlanId, etc.).

     To update: in Shopify admin go to Products → Matter → click into each
     selling plan group. The plan ID is the long number in the URL after
     /selling_plan_groups/.

     Keys are <frequency>-<term>:
       monthly-1   = single-box monthly
       monthly-3   = prepay 3 boxes upfront, then renews every 3 boxes
       monthly-6   = prepay 6 boxes upfront, then renews every 6 boxes
       monthly-12  = prepay 12 boxes upfront, then renews every 12 boxes
       quarterly-1 = single-box quarterly
       quarterly-4 = prepay 4 boxes upfront, then renews every 4 boxes (annual)
     ============================================================ */
  const PLAN_OVERRIDES = {
    'monthly-1':   5657526557,
    'monthly-3':   5657559325,
    'monthly-6':   5657592093,
    'monthly-12':  5657624861,
    'quarterly-1': 5657788701,
    'quarterly-4': 5657821469,
  };

  document.querySelectorAll('.matter-section').forEach(initMatterSection);

  function initMatterSection(root) {
    const dataEl = root.querySelector('[data-selling-plans]');
    if (!dataEl) return;

    let planData;
    try {
      planData = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.warn('[Matter] could not parse selling-plan data', e);
      planData = { basePrice: 0, currency: 'USD', groups: [] };
    }

    const state = {
      freq: 'monthly',
      term: '1',
      basePrice: planData.basePrice,
      currency: planData.currency,
      groups: planData.groups,
      // Discount % per term — read from data attrs on .m-term elements
      discounts: collectDiscounts(root),
      // Selected upsells: { [variantId]: priceFloat }
      upsells: {}
    };

    wireFreqTiles(root, state);
    wireTermTiles(root, state);
    wireUpsells(root, state);
    wireVideo(root);
    wireForm(root, state);
    filterTermsForFreq(root, state);
    update(root, state);
  }

  /* ---- Read discounts from rendered DOM (set by Liquid from theme settings) ---- */
  function collectDiscounts(root) {
    const map = {};
    root.querySelectorAll('.m-term').forEach((el) => {
      const term = el.dataset.term;
      const txt = (el.querySelector('.m-term__save')?.textContent || '').match(/(\d+(?:\.\d+)?)\s*%/);
      map[term] = txt ? parseFloat(txt[1]) / 100 : 0;
    });
    return map;
  }

  /* ---- Frequency tiles ---- */
  function wireFreqTiles(root, state) {
    const tiles = root.querySelectorAll('.m-freq');
    tiles.forEach((tile) => {
      tile.addEventListener('click', () => {
        tiles.forEach((t) => t.classList.remove('m-freq--is-active'));
        tile.classList.add('m-freq--is-active');
        const input = tile.querySelector('input');
        if (input) input.checked = true;
        state.freq = tile.dataset.freq;
        filterTermsForFreq(root, state);
        update(root, state);
      });
    });
  }

  /* ---- Filter term tiles based on which frequency is selected ----
     We use the PLAN_OVERRIDES keys as the source of truth for which
     (freq, term) combos are real. Tiles for nonexistent combos hide,
     and if the user had selected an invalid term we fall back to
     the first available one for the new frequency. */
  function filterTermsForFreq(root, state) {
    const tiles = root.querySelectorAll('.m-term');
    const availableTerms = [];
    tiles.forEach((tile) => {
      const term = tile.dataset.term;
      const key = state.freq + '-' + term;
      // Hide if the overrides exist and this combo isn't in them.
      // (When overrides are empty, show all — fallback for partial setups.)
      const hasOverrides = Object.keys(PLAN_OVERRIDES).length > 0;
      const isValid = !hasOverrides || PLAN_OVERRIDES[key] != null;
      if (isValid) {
        tile.removeAttribute('hidden');
        tile.style.display = '';
        availableTerms.push(term);
      } else {
        tile.setAttribute('hidden', '');
        tile.style.display = 'none';
        tile.classList.remove('m-term--is-active');
        const input = tile.querySelector('input');
        if (input) input.checked = false;
      }
    });
    // If the current term isn't valid for this freq, pick the first one that is.
    if (availableTerms.length && !availableTerms.includes(state.term)) {
      state.term = availableTerms[0];
      tiles.forEach((tile) => {
        if (tile.dataset.term === state.term) {
          tile.classList.add('m-term--is-active');
          const input = tile.querySelector('input');
          if (input) input.checked = true;
        } else {
          tile.classList.remove('m-term--is-active');
        }
      });
    }
  }

  /* ---- Term tiles ---- */
  function wireTermTiles(root, state) {
    const tiles = root.querySelectorAll('.m-term');
    tiles.forEach((tile) => {
      tile.addEventListener('click', () => {
        if (tile.hasAttribute('hidden')) return;
        tiles.forEach((t) => t.classList.remove('m-term--is-active'));
        tile.classList.add('m-term--is-active');
        const input = tile.querySelector('input');
        if (input) input.checked = true;
        state.term = tile.dataset.term;
        update(root, state);
      });
    });
  }

  /* ---- Upsell toggles ---- */
  function wireUpsells(root, state) {
    root.querySelectorAll('.m-upsell__card').forEach((card) => {
      const cb = card.querySelector('[data-upsell-toggle]');
      if (!cb) return;
      const variantId = cb.dataset.variantId;
      const price = parseFloat(cb.dataset.price || '0');
      if (!variantId) return;

      card.addEventListener('click', (e) => {
        // Don't toggle if click was on a real input/button inside
        if (e.target.tagName === 'A' || e.target.tagName === 'BUTTON') return;
        cb.checked = !cb.checked;
        if (cb.checked) {
          state.upsells[variantId] = price;
          card.classList.add('is-selected');
        } else {
          delete state.upsells[variantId];
          card.classList.remove('is-selected');
        }
        update(root, state);
      });
    });
  }

  /* ---- Video play button ---- */
  function wireVideo(root) {
    const frame = root.querySelector('.m-video__frame');
    const playBtn = root.querySelector('[data-video-play]');
    const embed = root.querySelector('[data-video-embed]');
    if (!frame || !playBtn || !embed) return;

    playBtn.addEventListener('click', () => {
      const url = embed.dataset.url;
      const file = embed.dataset.file;

      if (file) {
        // Uploaded MP4
        embed.innerHTML = `<video src="${file}" controls autoplay playsinline></video>`;
      } else if (url) {
        const embedUrl = toEmbedUrl(url);
        if (embedUrl) {
          embed.innerHTML = `<iframe src="${embedUrl}" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`;
        }
      }
      frame.classList.add('is-playing');
    });
  }

  /* Convert YouTube/Vimeo URL to embeddable form with autoplay */
  function toEmbedUrl(url) {
    try {
      const u = new URL(url);
      // YouTube
      const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
      if (ytMatch) {
        return `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1&rel=0`;
      }
      // Vimeo
      const vmMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
      if (vmMatch) {
        return `https://player.vimeo.com/video/${vmMatch[1]}?autoplay=1`;
      }
      return null;
    } catch {
      return null;
    }
  }

  /* ---- Form submission ---- */
  function wireForm(root, state) {
    const form = root.querySelector('[data-cart-form]');
    if (!form) return;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const planId = resolveSellingPlanId(state);
      if (!planId) {
        const lbl = root.querySelector('[data-summary-label]');
        if (lbl) lbl.textContent = 'Subscription unavailable — contact support';
        console.warn('[Matter] no selling plan matched for', state.freq, state.term);
        return;
      }

      form.querySelector('[data-selling-plan-input]').value = planId;

      const cta = root.querySelector('[data-summary-cta]');
      const originalLabel = cta?.innerHTML;
      if (cta) { cta.disabled = true; cta.innerHTML = 'Adding to cart…'; }

      try {
        const variantId = form.querySelector('[name="id"]').value;
        const items = [{
          id: parseInt(variantId, 10),
          quantity: 1,
          selling_plan: parseInt(planId, 10)
        }];

        // Append any selected upsells as separate line items.
        Object.keys(state.upsells).forEach((vid) => {
          if (vid && parseInt(vid, 10)) {
            items.push({ id: parseInt(vid, 10), quantity: 1 });
          }
        });

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items })
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || err.message || 'Add to cart failed');
        }

        // Refresh cart count in utility nav before redirecting (in case user has the bar)
        refreshCartCount();

        // Send to checkout. Swap to '/cart' if you prefer a cart review step.
        window.location.href = '/checkout';
      } catch (err) {
        console.error('[Matter] cart error:', err);
        if (cta) {
          cta.disabled = false;
          cta.innerHTML = 'Try again →';
        }
        alert('Sorry, we couldn\'t add that to your cart. Please refresh and try again.');
      }
    });
  }

  /* ---- Cart count refresh (utility nav) ---- */
  async function refreshCartCount() {
    try {
      const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count || '';
        el.setAttribute('data-cart-count', cart.item_count || 0);
      });
    } catch {
      // silent fail
    }
  }

  /* ---- Map (freq, term) → selling plan ID ---- */
  /* PLAN_OVERRIDES is defined at the top of the IIFE — see there for the
     real plan IDs and key naming. The keyword-matcher below is a fallback
     for installs that haven't filled it in. */

  function resolveSellingPlanId(state) {
    const key = state.freq + '-' + state.term;
    if (PLAN_OVERRIDES[key]) return PLAN_OVERRIDES[key];

    const allPlans = state.groups.flatMap((g) =>
      g.selling_plans || g.plans || []
    );
    if (!allPlans.length) return null;

    const wantFreq = state.freq === 'monthly' ? /\bmonth/i : /\bquarter/i;
    const wantTermNum = parseInt(state.term, 10);

    // Score every plan; pick best match.
    const scored = allPlans.map((p) => {
      const text = ((p.name || '') + ' ' + (p.description || '')).toLowerCase();
      let score = 0;

      if (wantFreq.test(text)) score += 10;

      // Look for term length. Patterns: "3 renewals", "3-month", "12 months",
      // "12 month prepay", "12-pack", etc.
      const numMatches = text.match(/\b(\d+)\b/g) || [];
      const nums = numMatches.map((n) => parseInt(n, 10));
      if (nums.includes(wantTermNum)) score += 8;

      // Special case for 1-renewal / pay-as-you-go.
      if (wantTermNum === 1 && /(pay[- ]as[- ]you[- ]go|no commitment|single)/i.test(text)) {
        score += 4;
      }

      return { plan: p, score, text };
    });

    scored.sort((a, b) => b.score - a.score);
    if (scored[0] && scored[0].score >= 10) return scored[0].plan.id;
    return null;
  }

  /* ---- Update summary UI ---- */
  function update(root, state) {
    const perBox = round2(state.basePrice * (1 - (state.discounts[state.term] || 0)));
    const subTotal = round2(perBox * parseInt(state.term, 10));
    const fullSubTotal = round2(state.basePrice * parseInt(state.term, 10));
    const subSavings = round2(fullSubTotal - subTotal);

    // Sum of selected upsells
    const upsellTotal = Object.values(state.upsells).reduce((sum, p) => sum + (p || 0), 0);
    const upsellCount = Object.keys(state.upsells).length;
    const totalToday = round2(subTotal + upsellTotal);

    const termNum = parseInt(state.term, 10);
    const freqLabel = state.freq === 'monthly' ? 'Monthly' : 'Quarterly';

    // What customer pays today and what renews
    // For monthly: term=1 → billed monthly. term=3 → 3-box prepay every 3 months. etc.
    // For quarterly: term=1 → billed quarterly. term=4 → 4-box prepay every 12 months.
    let termLabel;
    let renewalText;
    if (termNum === 1) {
      termLabel = state.freq === 'monthly' ? 'pay per box, monthly' : 'pay per box, quarterly';
      renewalText = state.freq === 'monthly'
        ? 'Renews every month at ' + formatMoney(perBox, state.currency)
        : 'Renews every 3 months at ' + formatMoney(perBox, state.currency);
    } else {
      // Prepay plans
      const intervalLabel = state.freq === 'monthly'
        ? termNum + (termNum === 1 ? ' month' : ' months')
        : (termNum * 3) + ' months'; // quarterly x4 = 12 months
      termLabel = termNum + '-box prepay';
      renewalText = 'Renews every ' + intervalLabel + ' at ' + formatMoney(subTotal, state.currency);
    }

    setText(root, '[data-summary-label]', freqLabel + ' · ' + termLabel);
    setText(root, '[data-summary-price]', formatMoney(perBox, state.currency));
    setText(root, '[data-summary-per]', '/ box');

    // Mirror price into the sticky mobile bar
    setText(root, '[data-stickybar-price]', formatMoney(perBox, state.currency));
    setText(root, '[data-stickybar-per]', '/ box');

    const totalEl = root.querySelector('[data-summary-total]');
    if (totalEl) {
      let html = '';
      if (termNum > 1) {
        // Prepay: show what they're charged today + what renews
        html = '<strong>Today:</strong> ' + formatMoney(totalToday, state.currency);
        html += ' <span class="m-summary__boxes">· ' + termNum + ' boxes</span>';
        if (subSavings > 0) {
          html += ' <span class="m-you-save">· you save ' + formatMoney(subSavings, state.currency) + '</span>';
        }
        html += '<br><span class="m-summary__renewal">' + renewalText + '</span>';
      } else {
        // Pay-per-box: simpler line
        html = '<strong>Today:</strong> ' + formatMoney(totalToday, state.currency);
        html += '<br><span class="m-summary__renewal">' + renewalText + '</span>';
      }
      if (upsellCount > 0) {
        html += ' <span class="m-you-add">(+' + upsellCount + ' add-on' + (upsellCount > 1 ? 's' : '') + ')</span>';
      }
      totalEl.innerHTML = html;
    }

    const cta = root.querySelector('[data-summary-cta]');
    if (cta && !cta.disabled) {
      const word = state.freq === 'monthly' ? 'Subscribe monthly' : 'Subscribe quarterly';
      cta.innerHTML = word + ' →';
    }
  }

  /* ---- Helpers ---- */
  function setText(root, sel, val) {
    const el = root.querySelector(sel);
    if (el) el.textContent = val;
  }
  function round2(n) { return Math.round(n * 100) / 100; }
  function formatMoney(amount, currency) {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD'
      }).format(amount);
    } catch {
      return '$' + amount.toFixed(2);
    }
  }
})();

/* ============================================================
   MATTER — URGENCY STRIP
   ------------------------------------------------------------
   Renders deterministic cutoff messaging and a scarcity counter
   that resets each month and decreases day-by-day to a floor.
   Every visitor sees the same number on the same calendar day
   because the PRNG is seeded by the month's start timestamp.
   ============================================================ */
(function () {
  'use strict';

  document.querySelectorAll('[data-urgency]').forEach(initUrgency);

  function initUrgency(root) {
    const cfg = {
      cutoffDay:  parseInt(root.dataset.cutoffDay  || '20', 10),
      minFloor:   parseInt(root.dataset.scarcityMin || '1', 10),
      maxFloor:   parseInt(root.dataset.scarcityMax || '2', 10),
      minStart:   parseInt(root.dataset.scarcityStartMin || '10', 10),
      maxStart:   parseInt(root.dataset.scarcityStartMax || '15', 10),
      cutoffPre:    root.dataset.cutoffPre   || "Order by the __DAY__ to receive this month's box.",
      cutoffToday:  root.dataset.cutoffToday || "Last day to order for this month's box.",
      cutoffNext:   root.dataset.cutoffNext  || "Next batch ships __MONTH__. Subscribe now to lock your spot.",
      scarcityTpl:  root.dataset.scarcityTemplate || "__COUNT__ boxes left in this batch"
    };

    const now = new Date();
    renderCutoff(root, now, cfg);
    renderScarcity(root, now, cfg);
  }

  /* ---- Cutoff message ---- */
  function renderCutoff(root, now, cfg) {
    const el = root.querySelector('[data-urgency-cutoff]');
    if (!el) return;

    const today = now.getDate();
    let msg;

    if (today < cfg.cutoffDay) {
      msg = cfg.cutoffPre.replace('__DAY__', ordinal(cfg.cutoffDay));
    } else if (today === cfg.cutoffDay) {
      msg = cfg.cutoffToday;
      root.classList.add('m-urgency--today');
    } else {
      // After cutoff — ships next month
      const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      const monthName = next.toLocaleDateString(undefined, { month: 'long' });
      msg = cfg.cutoffNext.replace('__MONTH__', monthName);
      root.classList.add('m-urgency--after');
    }
    el.textContent = msg;
  }

  /* ---- Scarcity counter ---- */
  function renderScarcity(root, now, cfg) {
    const el = root.querySelector('[data-urgency-scarcity]');
    if (!el) return;

    // The cycle runs from (cutoffDay + 1) of month N through cutoffDay of month N+1.
    // Determine which cycle we're in, its start date, and our day offset within it.
    const cutoff = cfg.cutoffDay;
    const today = now.getDate();
    let cycleStart;
    if (today > cutoff) {
      // Cycle started yesterday-ish (the day after this month's cutoff)
      cycleStart = new Date(now.getFullYear(), now.getMonth(), cutoff + 1);
    } else {
      // We're still in a cycle that began the day after last month's cutoff
      cycleStart = new Date(now.getFullYear(), now.getMonth() - 1, cutoff + 1);
    }

    // Cycle end is the cutoff of the month after cycleStart
    const cycleEnd = new Date(cycleStart.getFullYear(), cycleStart.getMonth() + 1, cutoff);
    const totalDays = Math.max(1, Math.round((cycleEnd - cycleStart) / 86400000));
    const dayOffset = Math.max(0, Math.min(totalDays, Math.round((now - cycleStart) / 86400000)));

    // Seed PRNG with the cycle's start date so every visitor sees the same numbers
    const seed = cycleStart.getFullYear() * 10000 + (cycleStart.getMonth() + 1) * 100 + cycleStart.getDate();
    const rand = mulberry32(seed);

    const startMin = Math.min(cfg.minStart, cfg.maxStart);
    const startMax = Math.max(cfg.minStart, cfg.maxStart);
    const floorMin = Math.min(cfg.minFloor, cfg.maxFloor);
    const floorMax = Math.max(cfg.minFloor, cfg.maxFloor);

    const starting = Math.round(startMin + rand() * (startMax - startMin));
    let floor = Math.round(floorMin + rand() * (floorMax - floorMin));
    // Ensure floor < starting always
    if (floor >= starting) floor = Math.max(1, starting - 2);

    const t = dayOffset / totalDays;
    const remaining = Math.max(floor, Math.round(starting - (starting - floor) * t));

    el.textContent = cfg.scarcityTpl.replace('__COUNT__', String(remaining));

    // Intensify visual when remaining is low
    if (remaining <= Math.max(3, floor + 2)) {
      root.classList.add('m-urgency--low');
    }
  }

  /* ---- Seeded PRNG (mulberry32) ---- */
  function mulberry32(seed) {
    let a = seed >>> 0;
    return function () {
      a |= 0; a = a + 0x6D2B79F5 | 0;
      let t = a;
      t = Math.imul(t ^ t >>> 15, t | 1);
      t ^= t + Math.imul(t ^ t >>> 7, t | 61);
      return ((t ^ t >>> 14) >>> 0) / 4294967296;
    };
  }

  /* ---- 1st / 2nd / 3rd / 4th ---- */
  function ordinal(n) {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  }
})();

/* ============================================================
   MATTER — STICKY MOBILE BAR VISIBILITY
   ------------------------------------------------------------
   Bar is hidden by default. Revealed after the hero scrolls
   out of view. Hidden again when the plans configurator is on
   screen (so we don't double up CTAs) or when the page is at
   the bottom (footer in view).
   ============================================================ */
(function () {
  'use strict';

  document.querySelectorAll('[data-stickybar]').forEach(initStickyBar);

  function initStickyBar(bar) {
    if (!('IntersectionObserver' in window)) {
      bar.setAttribute('data-visible', 'true');
      return;
    }

    const section = bar.closest('.matter-section');
    if (!section) return;

    const hero = section.querySelector('.m-hero');
    const plans = section.querySelector('.m-plans');

    let heroOut = false;
    let plansIn = false;

    const update = () => {
      const visible = heroOut && !plansIn;
      bar.setAttribute('data-visible', visible ? 'true' : 'false');
      bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
    };

    if (hero) {
      new IntersectionObserver(([e]) => {
        // Hide bar while >40% of hero still visible
        heroOut = e.intersectionRatio < 0.6;
        update();
      }, { threshold: [0, 0.6, 1] }).observe(hero);
    } else {
      heroOut = true;
    }

    if (plans) {
      new IntersectionObserver(([e]) => {
        plansIn = e.isIntersecting && e.intersectionRatio > 0.25;
        update();
      }, { threshold: [0, 0.25, 0.5] }).observe(plans);
    }

    update();
  }
})();
